<template>
  <div v-if="servicePresets.length" class="service-bar-wrap" :class="{ 'is-open': open }">
    <button
      v-if="!open"
      type="button"
      class="service-bar-toggle"
      title="Pengaturan countdown"
      @click="open = true"
    >
      <i class="fas fa-cog" />
    </button>

    <div v-else class="service-bar">
      <span class="service-bar__label">Countdown</span>
      <div class="service-bar__presets">
        <button
          v-for="preset in servicePresets"
          :key="preset.id"
          type="button"
          class="service-bar__btn"
          :class="{ 'is-active': preset.id === activeServiceId }"
          @click="activate(preset.id)"
        >
          {{ preset.name }}
        </button>
      </div>
      <div class="service-bar__controls">
        <button
          type="button"
          class="btn btn-sm"
          :class="serviceSessionRunning ? 'btn-warning' : 'btn-success'"
          @click="toggleServiceCountdown"
        >
          <i class="fas" :class="serviceSessionRunning ? 'fa-pause' : 'fa-play'" />
          {{ serviceSessionRunning ? 'Pause' : 'Start' }}
        </button>
        <button type="button" class="btn btn-sm btn-outline-secondary" title="Reset countdown" @click="resetServiceCountdown">
          <i class="fas fa-redo" />
        </button>
        <span class="service-bar__time">{{ countdownDisplay }}</span>
        <span v-if="activeService?.tMinusMode === 'wallclock'" class="service-bar__target">
          @ {{ activeService.tMinusTargetTime }}
        </span>
        <button type="button" class="btn btn-sm btn-outline-secondary" title="Edit preset" @click="openSettings">
          <i class="fas fa-cog" />
        </button>
        <button type="button" class="btn btn-sm btn-outline-secondary" title="Hide" @click="open = false">
          <i class="fas fa-times" />
        </button>
      </div>
    </div>
    <ServicePresetModal />
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import ServicePresetModal from './ServicePresetModal.vue'

export default {
  components: { ServicePresetModal },
  data() {
    return {
      open: false,
      tickTimer: null,
    }
  },
  computed: {
    ...mapGetters('workspace', [
      'servicePresets',
      'activeServiceId',
      'activeService',
      'serviceCountdownRemaining',
      'serviceSessionRunning',
      'layout',
    ]),
    countdownDisplay() {
      const s = Math.abs(this.serviceCountdownRemaining || 0)
      const h = Math.floor(s / 3600)
      const m = Math.floor((s % 3600) / 60)
      const sec = s % 60
      const prefix = (this.serviceCountdownRemaining || 0) < 0 ? 'T+ ' : 'T− '
      if (h > 0) {
        return `${prefix}${h}:${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`
      }
      return `${prefix}${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`
    },
    needTick() {
      return this.serviceSessionRunning || this.activeService?.tMinusMode === 'wallclock'
    },
  },
  watch: {
    needTick: {
      immediate: true,
      handler(need) {
        if (need) this.startTick()
        else this.stopTick()
      },
    },
  },
  beforeDestroy() {
    this.stopTick()
  },
  methods: {
    ...mapActions('workspace', [
      'activateService',
      'startServiceCountdown',
      'pauseServiceCountdown',
      'resetServiceCountdown',
      'toggleServiceCountdown',
      'tickServiceCountdown',
    ]),
    activate(id) {
      this.activateService(id)
    },
    openSettings() {
      this.$modal.show('service-presets', { global: this.layout?.global })
    },
    startTick() {
      this.stopTick()
      this.tickTimer = setInterval(() => {
        this.tickServiceCountdown()
      }, 1000)
    },
    stopTick() {
      if (this.tickTimer) {
        clearInterval(this.tickTimer)
        this.tickTimer = null
      }
    },
  },
}
</script>

<style scoped>
.service-bar-wrap {
  margin-bottom: 0;
}
.service-bar-toggle {
  appearance: none;
  width: 40px;
  min-height: 40px;
  height: auto;
  border: 1px solid var(--ws-widget-border, #e9ecef);
  border-right: 0;
  border-radius: 8px 0 0 8px;
  background: var(--ws-widget-header-bg, #f8f9fa);
  color: var(--ws-text-muted, #6c757d);
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.service-bar-toggle:hover {
  color: var(--ws-text, #212529);
  background: var(--ws-ctrl-hover, #e9ecef);
}
.service-bar {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.5rem 0.75rem;
  padding: 0.5rem 0.75rem;
  border: 1px solid var(--ws-panel-border, #dee2e6);
  border-radius: 8px;
  background: var(--ws-widget-bg, #fff);
}
.service-bar__label {
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--ws-text-muted, #6c757d);
}
.service-bar__presets {
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem;
  flex: 1;
  min-width: 0;
}
.service-bar__btn {
  border: 1px solid var(--ws-panel-border, #dee2e6);
  background: var(--ws-ctrl-bg, #f8f9fa);
  color: var(--ws-text, #212529);
  border-radius: 999px;
  padding: 0.25rem 0.75rem;
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;
}
.service-bar__btn.is-active {
  background: var(--ws-accent, #128c7e);
  border-color: var(--ws-accent, #128c7e);
  color: #fff;
}
.service-bar__controls {
  display: flex;
  align-items: center;
  gap: 0.35rem;
  flex-wrap: wrap;
}
.service-bar__time {
  font-size: 0.85rem;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  min-width: 4.5rem;
  text-align: center;
  color: var(--ws-text, #212529);
}
.service-bar__target {
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--ws-text-muted, #6c757d);
}
</style>
